import { Button, IconCheckmarkCircle, IconChevronRight, Modal, ProgressIndicator } from "@veneer/core";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import "../../assets/css/views/support/SupportActivity.css";
import supportService from "../../services/supportApi/support.service";
import parse, { DOMNode, Element } from "html-react-parser";
import { HtmlImageRendererWeb } from "./components/HtmlImageRendererWeb";
import SupportTicketForm from "./SupportTicketForm";
import { RootState } from "../../app/store";
import { ModulesEnum } from "../../models/enum/ModulesEnum";
import { Item } from "../../features/routeState/useRouteState";

const SupportActivity = () => {
  const [t, i18n] = useTranslation("global");
  const enabledModules: any[] = useSelector((state: RootState) => state.enableRoute.enabled);
  const canCreateTicket = enabledModules[0]?.some((e: Item) => e.id === ModulesEnum.CreateTicket) ?? false;
  const [step, setStep] = useState(0);
  const [path, setPath] = useState<any[]>([]);
  const [questions, setQuestions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showTicketForm, setShowTicketForm] = useState(false);

  const renderWithCustomImages = (htmlString: string) =>
    parse(htmlString, {
      replace: (domNode: DOMNode) => {
        if (
          domNode &&
          typeof domNode === "object" &&
          "attribs" in domNode
        ) {
          const element = domNode as Element;
          // Strip CMS inline styles so HP Veneer typography applies
          delete element.attribs.style;
          delete element.attribs.color;
          delete element.attribs.bgcolor;

          if (
            element.name === "img"
          ) {
            const src = element.attribs?.src;
            const alt = element.attribs?.alt || "";
            return <HtmlImageRendererWeb src={src} alt={alt} />;
          }
        }
      },
    });

  const getSupportData = async () => {
    setLoading(true);
    try {
      const response = await supportService.getSupportList(Number(t("header.idLanguage")), i18n.language);
      if (Array.isArray(response)) {
        setQuestions(response);
      }
    } catch (error) {
      console.error("Error fetching support data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSupportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const handleSelect = (item: any) => {
    setPath([...path, item]);
    setStep(step + 1);
  };

  const handleBack = () => {
    setPath(path.slice(0, -1));
    setStep(step - 1);
  };

  const handleRestart = () => {
    setPath([]);
    setStep(0);
  };

  const renderQuestionTitle = () => {
    if (step === 0) return t("support.titleStep0");
    if (step === 1)
      return t("support.titleStep1", { name: path[0].categoryName.toLowerCase() });
    if (step === 2 && path[1]?.subQuestions)
      return t("support.titleStep2");
    return t("support.titleAnswer");
  };

  const renderTimeline = () => (
    <div className="support-breadcrumb">
      {path.map((stepItem, idx) => (
        <span key={stepItem.categoryName || stepItem.title} className="support-breadcrumb-item">
          {idx > 0 && <IconChevronRight size={14} className="support-breadcrumb-sep" />}
          <IconCheckmarkCircle size={14} />
          <span>{stepItem.categoryName || stepItem.title}</span>
        </span>
      ))}
    </div>
  );

  const renderOptions = (items: any[]) =>
    items.map((item, idx) => (
      <div
        key={idx}
        className="support-option"
        onClick={() => handleSelect(item)}
      >
        <IconChevronRight />
        <span>{item.categoryName || item.title}</span>
      </div>
    ));

  const renderResponses = () => {
    const responses = path[2]?.responses || path[1]?.responses || [];
    return (
      <div className="support-responses">
        {responses.map((resp: any, idx: number) => (
          <div key={idx} className="response-block">
            {resp.type === "text" && <p>{resp.CONTENT}</p>}
            {resp.type === "rich-text" && renderWithCustomImages(resp.CONTENT)}
          </div>
        ))}
      </div>
    );
  };

  const renderContent = () => {
    if (loading) return (
      <div className="support-loading">
        <ProgressIndicator appearance="circular" behavior="indeterminate" />
      </div>
    );

    if (step === 0) return renderOptions(questions);
    if (step === 1) return renderOptions(path[0]?.questions || []);
    if (step === 2 && path[1]?.subQuestions)
      return renderOptions(path[1].subQuestions);
    return renderResponses();
  };

  return (
    <div className="support-container">
      <div className="support-page-content">
      <div className="support-page-header">
        <h4>{t("header.support")}</h4>
        {step > 0 && (
          <div className="support-card-header-actions">
            <Button appearance="ghost" onClick={handleBack}>
              {t("support.back")}
            </Button>
            <Button appearance="secondary" onClick={handleRestart}>
              {t("support.restart")}
            </Button>
          </div>
        )}
      </div>

      {path.length > 0 && renderTimeline()}

        <div className="support-body">
          <p className="support-title">{renderQuestionTitle()}</p>
          <div className="support-content">{renderContent()}</div>
        </div>
        {step === 0 && canCreateTicket && (
          <div className="support-contact-cta">
            <Button
              appearance="secondary"
              onClick={() => setShowTicketForm(true)}
            >
              {t("support.contactUs")}
            </Button>
          </div>
        )}
      </div>

      <Modal
        align="center"
        id="support-ticket-modal"
        closeButton
        show={showTicketForm}
        onClose={() => setShowTicketForm(false)}
        title={t("support.contactModalTitle")}
      >
        <SupportTicketForm key={String(showTicketForm)} onBack={() => setShowTicketForm(false)} />
      </Modal>
    </div>
  );
};

export default SupportActivity;
